/**
 * An on-screen overlay that prints the raw Gamepad API state, enabled with the
 * `?debug=1` query parameter.
 *
 * The target device is an Android phone with no developer console available,
 * so everything that would normally be a console.log goes on the screen
 * instead: the id and mapping string each controller reports, every axis
 * value, every button value, which buttons have been pressed since the last
 * reset, the connect/disconnect event log, and the direction/power-hit values
 * the game actually derives from all of that.
 */
'use strict';
import { gamepadManager, readGamepad, AXIS_DEADZONE } from './gamepad.js';

/** @constant @type {string} */
const OVERLAY_ID = 'gamepad-debug-overlay';

/**
 * Whether the debug overlay was requested via the query string.
 * @returns {boolean}
 */
export function isDebugOverlayRequested() {
  try {
    return new URLSearchParams(window.location.search).get('debug') === '1';
  } catch (err) {
    return /[?&]debug=1(&|$)/.test(window.location.search);
  }
}

/**
 * Create the overlay and start updating it. Safe to call more than once.
 */
export function setUpGamepadDebugOverlay() {
  if (document.getElementById(OVERLAY_ID) !== null) {
    return;
  }

  const root = document.createElement('div');
  root.id = OVERLAY_ID;
  root.setAttribute(
    'style',
    [
      'position:fixed',
      'top:0',
      'left:0',
      'right:0',
      'z-index:2147483647',
      'max-height:55vh',
      'overflow-y:auto',
      '-webkit-overflow-scrolling:touch',
      'background:rgba(0,0,0,0.88)',
      'color:#7CFC7C',
      "font-family:'DejaVu Sans Mono',Menlo,Consolas,monospace",
      'font-size:11px',
      'line-height:1.35',
      'padding:4px 6px 6px 6px',
      'white-space:pre-wrap',
      'word-break:break-all',
      'text-align:left',
    ].join(';')
  );

  const bar = document.createElement('div');
  bar.setAttribute(
    'style',
    'display:flex;gap:4px;align-items:center;margin-bottom:4px;flex-wrap:wrap'
  );

  const title = document.createElement('span');
  title.textContent = 'GAMEPAD DEBUG';
  title.setAttribute(
    'style',
    'color:#FFD700;font-weight:bold;margin-right:4px'
  );
  bar.appendChild(title);

  const swapBtn = makeButton('交換 P1/P2', () => gamepadManager.toggleSwap());
  const resetBtn = makeButton('清除已按紀錄', () => {
    seenButtons.clear();
    gamepadManager.eventLog.length = 0;
  });
  const hideBtn = makeButton('隱藏', () => setCollapsed(true));
  bar.appendChild(swapBtn);
  bar.appendChild(resetBtn);
  bar.appendChild(hideBtn);
  root.appendChild(bar);

  const body = document.createElement('div');
  root.appendChild(body);
  document.body.appendChild(root);

  // A small always-available handle to bring the overlay back.
  const showBtn = makeButton('🎮', () => setCollapsed(false));
  showBtn.setAttribute(
    'style',
    showBtn.getAttribute('style') +
      ';position:fixed;top:2px;left:2px;z-index:2147483647;display:none'
  );
  document.body.appendChild(showBtn);

  /**
   * @param {boolean} collapsed
   */
  function setCollapsed(collapsed) {
    root.style.display = collapsed ? 'none' : 'block';
    showBtn.style.display = collapsed ? 'block' : 'none';
  }

  /**
   * Buttons pressed at least once since the last reset, keyed by
   * "<gamepad index>" and holding the set of button indices.
   * @type {Map<number, Set<number>>}
   */
  const seenButtons = new Map();

  const startedAt = Date.now();
  let frames = 0;

  const update = () => {
    frames++;
    gamepadManager.refresh();
    recordSeenButtons(gamepadManager.snapshot, seenButtons);
    if (root.style.display !== 'none') {
      body.textContent = render(seenButtons, startedAt, frames);
    }
    window.requestAnimationFrame(update);
  };
  window.requestAnimationFrame(update);
}

/**
 * Make a tap-friendly button for the overlay bar.
 * @param {string} label
 * @param {function():void} onClick
 * @returns {HTMLButtonElement}
 */
function makeButton(label, onClick) {
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = label;
  button.setAttribute(
    'style',
    [
      'background:#222',
      'color:#7CFC7C',
      'border:1px solid #7CFC7C',
      'border-radius:3px',
      'font-family:inherit',
      'font-size:11px',
      'padding:3px 7px',
      'cursor:pointer',
      'touch-action:manipulation',
    ].join(';')
  );
  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    onClick();
  });
  return button;
}

/**
 * Remember which buttons have been pressed at least once.
 * @param {Gamepad[]} pads
 * @param {Map<number, Set<number>>} seenButtons
 */
function recordSeenButtons(pads, seenButtons) {
  for (const pad of pads) {
    let seen = seenButtons.get(pad.index);
    if (seen === undefined) {
      seen = new Set();
      seenButtons.set(pad.index, seen);
    }
    for (let i = 0; i < pad.buttons.length; i++) {
      const button = pad.buttons[i];
      const pressed =
        typeof button === 'number'
          ? button > 0.5
          : button.pressed === true || button.value > 0.5;
      if (pressed) {
        seen.add(i);
      }
    }
  }
}

/**
 * Build the whole text body of the overlay.
 * @param {Map<number, Set<number>>} seenButtons
 * @param {number} startedAt
 * @param {number} frames
 * @returns {string}
 */
function render(seenButtons, startedAt, frames) {
  const lines = [];
  const pads = gamepadManager.snapshot;
  const seconds = Math.max(1, Math.round((Date.now() - startedAt) / 1000));

  lines.push(
    `overlay ${frames}f / ${seconds}s   secureContext=${
      window.isSecureContext
    }   getGamepads=${typeof navigator.getGamepads === 'function'}`
  );
  lines.push(`UA: ${navigator.userAgent}`);
  if (gamepadManager.lastError !== null) {
    lines.push(`!! getGamepads error: ${gamepadManager.lastError}`);
  }
  lines.push(
    `已連線手把: ${pads.length}   插槽對應: ${
      gamepadManager.swapped ? '已交換 (P1=第2支)' : '正常 (P1=第1支)'
    }`
  );
  lines.push('');

  // What the game actually feeds into the physics engine.
  lines.push('--- 遊戲實際收到的輸入 ---');
  for (const slot of [0, 1]) {
    const pad = gamepadManager.getPadForSlot(slot);
    if (pad === null) {
      lines.push(`P${slot + 1}: (無手把)`);
      continue;
    }
    const state = readGamepad(pad);
    lines.push(
      `P${slot + 1}: x=${pad2(state.xDirection)} y=${pad2(
        state.yDirection
      )} powerHit=${state.powerHitIsDown ? 'DOWN' : '.   '} 來源=${
        state.dpadSource
      }  <- pad#${pad.index}`
    );
  }
  lines.push('');

  if (pads.length === 0) {
    lines.push('--- 尚未偵測到任何手把 ---');
    lines.push('Chrome 要在手把上「按一個鍵」之後才會把它列出來，');
    lines.push('所以先按 A 鍵幾下再看這裡。兩支都要各自按過。');
    lines.push('');
  }

  for (const pad of pads) {
    lines.push(`--- pad#${pad.index} ---`);
    lines.push(`id      : ${pad.id}`);
    lines.push(
      `mapping : "${pad.mapping}"   connected=${pad.connected}   axes=${pad.axes.length} buttons=${pad.buttons.length}`
    );

    const axisLines = [];
    for (let i = 0; i < pad.axes.length; i++) {
      const value = pad.axes[i];
      const active = Math.abs(value) >= AXIS_DEADZONE;
      axisLines.push(
        `a${String(i).padStart(2, '0')}=${fixed(value)}${active ? '*' : ' '}`
      );
    }
    lines.push(`axes    : ${axisLines.join(' ') || '(none)'}`);

    const pressedNow = [];
    const buttonLines = [];
    for (let i = 0; i < pad.buttons.length; i++) {
      const button = pad.buttons[i];
      const value = typeof button === 'number' ? button : button.value;
      const pressed =
        typeof button === 'number'
          ? button > 0.5
          : button.pressed === true || button.value > 0.5;
      if (pressed) {
        pressedNow.push(i);
      }
      buttonLines.push(
        `b${String(i).padStart(2, '0')}=${fixed(value)}${pressed ? '*' : ' '}`
      );
    }
    lines.push(`按下中 : ${pressedNow.length ? pressedNow.join(',') : '(無)'}`);
    const seen = seenButtons.get(pad.index);
    lines.push(
      `曾按下 : ${
        seen && seen.size ? [...seen].sort((a, b) => a - b).join(',') : '(無)'
      }`
    );
    lines.push(`buttons : ${buttonLines.join(' ') || '(none)'}`);
    lines.push('');
  }

  lines.push('--- 連線事件 ---');
  if (gamepadManager.eventLog.length === 0) {
    lines.push('(尚無事件)');
  } else {
    for (const entry of gamepadManager.eventLog) {
      lines.push(entry);
    }
  }

  return lines.join('\n');
}

/**
 * Format a number to two decimals with a sign column, so the columns stay put.
 * @param {number} value
 * @returns {string}
 */
function fixed(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return ' ????';
  }
  const text = value.toFixed(2);
  return value < 0 ? text : ` ${text}`;
}

/**
 * Format -1/0/1 in a fixed-width column.
 * @param {number} value
 * @returns {string}
 */
function pad2(value) {
  return value < 0 ? '-1' : ` ${value}`;
}
